import PageLayout from "../../components/PageLayout";
import { useResponsive } from "../../lib/useResponsive";

export default function EntryValuationPage() {
  const { isMobile } = useResponsive();

  return (
    <PageLayout
      eyebrow="Entry Valuation"
      title={<>How your <em>entry price</em> is set.</>}
      subtitle="SSN offers two valuation options — rules differ by member type."
    >
      {/* Option 1 Card */}
      <div style={S.valCard}>
        <div style={{ ...S.badgeRow, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ ...S.valBadge, ...S.greyBadge }}>Option 1 — Merit-Based Only</div>
          <div style={{ ...S.whoTag, ...S.darkGreenTag }}>⭐ Qualifying SSN Members Only</div>
        </div>
        <h3 style={S.cardH3}>Discounted Entry Price</h3>
        <p style={S.vdesc}>Selected members receive a % discount on unit price — more units for the same investment.</p>
        
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ ...S.miniT, minWidth: isMobile ? 280 : "auto" }}>
            <tbody>
              <tr><td style={S.td}>Portfolio value</td><td style={S.valText}><strong>$1,000,000</strong></td></tr>
              <tr><td style={S.td}>10% discount applied</td><td style={S.valText}><strong>- $100,000</strong></td></tr>
              <tr><td style={S.td}>Adjusted base</td><td style={S.valText}><strong>$900,000</strong></td></tr>
              <tr><td style={S.td}>Units in circulation</td><td style={S.valText}><strong>1,000</strong></td></tr>
              <tr><td style={S.td}>Discounted unit price</td><td style={S.valText}><strong>$900 / unit</strong></td></tr>
              <tr><td style={S.td}>You invest</td><td style={S.valText}><strong>$25,000</strong></td></tr>
              <tr style={S.resRow}>
                <td style={S.td}>Units you receive</td>
                <td style={{ ...S.valText, ...S.highlightedOrange }}><strong>27.78 vs 25 🎉</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Option 2 Card */}
      <div style={S.valCard}>
        <div style={{ ...S.badgeRow, flexDirection: isMobile ? "column" : "row" }}>
          <div style={{ ...S.valBadge, ...S.greyBadge }}>Option 2 — Available to All</div>
          <div style={{ ...S.whoTag, ...S.lightGreenTag }}>✅ All Investors</div>
        </div>
        <h3 style={S.cardH3}>Future Date Lock</h3>
        <p style={S.vdesc}>Deposit now, lock your unit price on a future date when the market dips.</p>
        
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ ...S.miniT, minWidth: isMobile ? 280 : "auto" }}>
            <tbody>
              <tr><td style={S.td}>You deposit on</td><td style={S.valText}><strong>June 10, 2026</strong></td></tr>
              <tr><td style={S.td}>Market drops ~5% on</td><td style={S.valText}><strong>September 1, 2026</strong></td></tr>
              <tr><td style={S.td}>Portfolio value Sep 1</td><td style={S.valText}><strong>$950,000</strong></td></tr>
              <tr><td style={S.td}>Units in circulation</td><td style={S.valText}><strong>1,000</strong></td></tr>
              <tr><td style={S.td}>Unit price on Sep 1</td><td style={S.valText}><strong>$950 / unit ✓</strong></td></tr>
              <tr><td style={S.td}>You invest</td><td style={S.valText}><strong>$25,000</strong></td></tr>
              <tr style={S.resRow}>
                <td style={S.td}>Units you receive</td>
                <td style={{ ...S.valText, ...S.highlightedOrange }}><strong>26.32 vs 25 🎉</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div style={S.valRule}>
          ⚠️ <strong>Same-day rule:</strong> If market drops on Sep 1, you <strong>must lock that day</strong>. Window closes end of day — every day.
        </div>
      </div>

      <div style={S.valInfoBox}>
        <div style={S.infoTag}>💡 KEY INSIGHT</div>
        <p style={S.boxText}>The real advantage is joining early. Every unit today at ~$1,700 was once $1,000. Future members will pay even more.</p>
      </div>
      
      <div style={S.valInfoBox}>
        <div style={S.infoTag}>📅 PRIOR VALUATION BENEFIT</div>
        <p style={S.boxText}>Qualifying ShivSena Exchange (SSE) members may use pricing from up to <strong>10 days prior</strong> to their investment date.</p>
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  valCard: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
    borderRadius: 12, padding: 24, marginBottom: 24
  },
  badgeRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "flex-start" },
  valBadge: { padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, letterSpacing: "0.02em" },
  greyBadge: { background: "rgba(255,255,255,0.08)", color: "var(--text-secondary)" },
  whoTag: { padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 },
  darkGreenTag: { background: "rgba(29,158,117,0.15)", color: "var(--teal-light)" },
  lightGreenTag: { background: "rgba(29,158,117,0.08)", color: "var(--text-primary)", border: "1px solid rgba(29,158,117,0.2)" },
  cardH3: { fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 },
  vdesc: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 20 },
  miniT: { width: "100%", borderCollapse: "collapse", fontSize: 14, marginBottom: 4 },
  td: { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", color: "var(--text-secondary)", whiteSpace: "nowrap" },
  valText: { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", textAlign: "right", color: "var(--text-primary)", whiteSpace: "nowrap" },
  resRow: { background: "rgba(29,158,117,0.04)" },
  highlightedOrange: { color: "#ffb04f" },
  valRule: { fontSize: 13, color: "var(--text-secondary)", background: "rgba(255, 166, 0, 0.04)", border: "1px solid rgba(255, 166, 0, 0.15)", padding: 12, borderRadius: 8, marginTop: 16, lineHeight: 1.5 },
  valInfoBox: {
    background: "rgba(255,255,255,0.02)", borderLeft: "3px solid var(--teal-light)",
    padding: "16px 20px", borderRadius: "0 8px 8px 0", marginBottom: 16
  },
  infoTag: { fontSize: 11, fontWeight: 700, color: "var(--teal-light)", letterSpacing: "0.05em", marginBottom: 6 },
  boxText: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6 }
};
